import type { UserAnswer, Question, ScoringRules } from '../types';

/**
 * 分数统计结果
 */
export interface ScoreResult {
  /** 总分 */
  totalScore: number;
  /** 得分 */
  score: number;
  /** 正确题数 */
  correctCount: number;
  /** 错误题数 */
  wrongCount: number;
  /** 总题数 */
  totalCount: number;
  /** 正确率 */
  accuracy: number;
  /** 是否通过 */
  passed: boolean;
  /** 用时（毫秒） */
  timeSpent: number;
}

/**
 * 分数计算器
 */
export class ScoreCalculator {
  /**
   * 使用自定义评分规则计算分数
   */
  static calculate(answers: UserAnswer[], questions: Question[], rules: ScoringRules): ScoreResult {
    const score = rules.calculateScore(answers, questions);
    const correctCount = answers.filter((a) => a.isCorrect).length;
    const totalCount = questions.length;
    const wrongCount = answers.filter((a) => !a.isCorrect).length;
    const timeSpent = answers.reduce((sum, a) => sum + a.timeSpent, 0);

    return {
      totalScore: rules.totalScore,
      score,
      correctCount,
      wrongCount,
      totalCount,
      accuracy: totalCount > 0 ? (correctCount / totalCount) * 100 : 0,
      passed: score >= rules.passingScore,
      timeSpent,
    };
  }

  /**
   * 简单计算（每题等分）
   */
  static simpleCalculate(
    answers: UserAnswer[],
    questions: Question[],
    totalScore: number = 100,
    passingScore: number = 60
  ): ScoreResult {
    const correctCount = answers.filter((a) => a.isCorrect).length;
    const totalCount = questions.length;
    const scorePerQuestion = totalScore / totalCount;
    const score = Math.round(correctCount * scorePerQuestion);
    const wrongCount = answers.filter((a) => !a.isCorrect).length;
    const timeSpent = answers.reduce((sum, a) => sum + a.timeSpent, 0);

    return {
      totalScore,
      score,
      correctCount,
      wrongCount,
      totalCount,
      accuracy: totalCount > 0 ? (correctCount / totalCount) * 100 : 0,
      passed: score >= passingScore,
      timeSpent,
    };
  }

  /**
   * 按难度加权计算
   */
  static weightedCalculate(
    answers: UserAnswer[],
    questions: Question[],
    weights: { A1: number; A2: number; B1: number; B2: number } = {
      A1: 1,
      A2: 1.2,
      B1: 1.5,
      B2: 1.8,
    }
  ): ScoreResult {
    const questionMap = new Map(questions.map((q) => [q.id, q]));

    let totalWeight = 0;
    let earnedWeight = 0;

    for (const answer of answers) {
      const question = questionMap.get(answer.questionId);
      if (!question) continue;

      // 使用 level 字段（A1/A2/B1/B2），如果未设置则使用默认值 A2
      const level = question.level;
      let normalizedDifficulty: keyof typeof weights = 'A2';
      if (level && level in weights) {
        normalizedDifficulty = level as keyof typeof weights;
      }

      const weight = weights[normalizedDifficulty];
      totalWeight += weight;

      if (answer.isCorrect) {
        earnedWeight += weight;
      }
    }

    const score = totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0;
    const correctCount = answers.filter((a) => a.isCorrect).length;
    const wrongCount = answers.filter((a) => !a.isCorrect).length;
    const timeSpent = answers.reduce((sum, a) => sum + a.timeSpent, 0);

    return {
      totalScore: 100,
      score,
      correctCount,
      wrongCount,
      totalCount: questions.length,
      accuracy: questions.length > 0 ? (correctCount / questions.length) * 100 : 0,
      passed: score >= 60,
      timeSpent,
    };
  }
}
